/* eslint-disable no-unused-vars */
import { COUNT_PER_PAGE, YN } from '../../constants/Constants';
import { PC_BASE_BLUEPRINT } from '../../constants/PerformanceCheckConstants';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  columnCheckState,
  columnNumberWithCommas,
  columnTransferState,
} from '../../utils/dataTableUtil';
import { useRecoilValue, useRecoilValueLoadable } from 'recoil';

import { Button } from 'primereact/button';
import CheckBaseDataDialog from '../../components/PerformanceCheck/CheckBaseDataDialog';
import { CodeUtil } from '../../utils/CodeUtil';
import { Column } from 'primereact/column';
import { CommonUtil } from '../../utils/commonUtil';
import { DataTable } from 'primereact/datatable';
import { InputText } from 'primereact/inputtext';
import { MultiSelect } from 'primereact/multiselect';
import { MyInfoUtil } from '../../utils/myInfoUtil';
import { Panel } from 'primereact/panel';
import { ProgressBar } from 'primereact/progressbar';
import { RadioButton } from 'primereact/radiobutton';
import RangeCalendar from '../../components/Common/RangeCalendar';
import RecheckBaseDataDialog from '../../components/PerformanceCheck/RecheckBaseDataDialog';
import SearchMenu from '../../components/Menu/SearchMenu';
import { ServiceProvider } from '../../services';
import { Tag } from 'primereact/tag';
import { TieredMenu } from 'primereact/tieredmenu';
import TransferState from '../../enums/TransferState';
import UserRoleType from '../../enums/UserRoleType';
import _ from 'lodash';
import { classNames } from 'primereact/utils';
import dayjs from 'dayjs';
import { myInfoSelector } from '../../recoil/selectors';
import {
  associationsList,
  enterpriseList,
  myInfoIds,
  shopListState,
  touchUIState,
} from '../../recoil/atoms';
import { useAuthorizedFetch } from '../../hooks/useAuthorizedFetch';
import CopyButton from '../../components/Common/CopyButton';
import PaymentDialog from '../../components/Dialog/PaymentDialog/PaymentDialog';
import { useHistory } from 'react-router-dom';
const performanceCheckService = ServiceProvider.performanceCheck;
const performanceCheckPaymentService = ServiceProvider.performanceCheckPayment;
const priceFormatter = new Intl.NumberFormat('ko-KR', { style: 'decimal' });

/** 성능점검 목록에서 선택·조회 가능한 점검장 (팀 / 오렌지 / 송이1·2) */
const PERFORMANCE_CHECK_LIST_SHOP_IDS = new Set([
  13, 15, 17, 18, 19, 20, 21,
]);

const LS_KEY_PERFORMANCE_CHECK_LIST_DATES =
  'cerp.performanceCheckList.dateRange';

function readPersistedPerformanceCheckListDates() {
  try {
    const raw = localStorage.getItem(LS_KEY_PERFORMANCE_CHECK_LIST_DATES);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const startDate = dayjs(parsed.startDate);
    const endDate = dayjs(parsed.endDate);
    if (!startDate.isValid() || !endDate.isValid()) return null;
    return { startDate: startDate.toDate(), endDate: endDate.toDate() };
  } catch {
    return null;
  }
}

function writePersistedPerformanceCheckListDates(startDate, endDate) {
  try {
    localStorage.setItem(
      LS_KEY_PERFORMANCE_CHECK_LIST_DATES,
      JSON.stringify({
        startDate: dayjs(startDate).toISOString(),
        endDate: dayjs(endDate).toISOString(),
      })
    );
  } catch {
    /* ignore */
  }
}

function clearPersistedPerformanceCheckListDates() {
  try {
    localStorage.removeItem(LS_KEY_PERFORMANCE_CHECK_LIST_DATES);
  } catch {
    /* ignore */
  }
}

function filterShopsForPerformanceCheckList(shopOptions) {
  return _.filter(shopOptions, (o) => {
    const v = o?.value;
    if (v === '' || v == null) return false;
    return PERFORMANCE_CHECK_LIST_SHOP_IDS.has(Number(v));
  });
}

function isAllowedPerformanceCheckListShopId(shopId) {
  if (shopId == null || shopId === '') return false;
  return PERFORMANCE_CHECK_LIST_SHOP_IDS.has(Number(shopId));
}

function formatPerformanceCheckListQueryParams(conditions) {
  const params = { ...conditions };
  for (const key in params) {
    if (key.toLowerCase().includes('date') && params[key]) {
      params[key] = dayjs(params[key]).format('YYYYMMDD');
    }
  }
  return params;
}

const PerformanceCheckList = () => {
  const shops = useRecoilValue(shopListState);
  const enterprises = useRecoilValue(enterpriseList);
  const associations = useRecoilValue(associationsList);
  const touchUI = useRecoilValue(touchUIState);
  const myInfoLoadable = useRecoilValueLoadable(myInfoSelector);
  const history = useHistory();
  const myInfo = useRecoilValue(myInfoIds);
  const { myAssociationId, myEnterpriseId, myShopId, myRoleCode, myShopName } =
    myInfo;

  const [dateRangeResetKey, setDateRangeResetKey] = useState(0);

  const DEFAULT_LAZY_PARAMS = {
    first: 0,
    rows: 10,
    page: 0,
  };

  const defaultSearchConditions = useMemo(() => {
    const permissions = {
      delete: _.filter(UserRoleType, function (r) {
        return (
          r.value === 'CERP_ADM' || r.value === 'A_MST' || r.value === 'A_ADM'
        );
      }),
      period: _.filter(UserRoleType, function (r) {
        return r.value === 'CERP_ADM' || _.startsWith(r.value, 'A_');
      }),
    };
    const deleteAuth =
      _.findIndex(permissions.delete, { value: myRoleCode }) >= 0;

    const today = dayjs();
    const persisted = readPersistedPerformanceCheckListDates();
    let startDate;
    let endDate;
    if (persisted) {
      startDate = persisted.startDate;
      endDate = persisted.endDate;
    } else {
      const useTodayOnly =
        myShopName?.includes('엔케이') ||
        isAllowedPerformanceCheckListShopId(myShopId);
      if (useTodayOnly) {
        startDate = today.toDate();
        endDate = today.toDate();
      } else {
        startDate = dayjs(today).subtract(1, 'month').toDate();
        endDate = today.toDate();
      }
    }

    return {
      associations: myAssociationId ? [myAssociationId] : [],
      enterprises: myEnterpriseId ? [myEnterpriseId] : [],
      shops:
        myShopId && isAllowedPerformanceCheckListShopId(myShopId)
          ? [myShopId]
          : [],
      startDate,
      endDate,
      statementNumber: '',
      carSearchKeyword: '',
      customerSearchBy: '1',
      customerSearchKeyword: '',
      checkType: '',
      delYN: deleteAuth ? '' : YN.NO,
    };
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    myAssociationId,
    myEnterpriseId,
    myShopId,
    myRoleCode,
    myShopName,
    dateRangeResetKey,
  ]);

  const [searchConditions, setSearchConditions] = useState({
    ...defaultSearchConditions,
  });
  const [lazyParams, setLazyParams] = useState({ ...DEFAULT_LAZY_PARAMS });

  const menu = useRef(null);
  const defaultSearchConditionsRef = useRef(defaultSearchConditions);
  defaultSearchConditionsRef.current = defaultSearchConditions;

  const { loading, totalRecords, data, getList } = useAuthorizedFetch(
    performanceCheckService,
    searchConditions,
    setSearchConditions,
    defaultSearchConditions,
    lazyParams.page,
    lazyParams.rows
  );

  const runSearchWithPersistedDates = useCallback(
    (conditions) => {
      collapseAllGroupRows();
      setExpandedGroupRows({});
      writePersistedPerformanceCheckListDates(
        conditions.startDate,
        conditions.endDate
      );
      getList(
        conditions,
        DEFAULT_LAZY_PARAMS.page,
        DEFAULT_LAZY_PARAMS.rows
      );
    },
    [getList]
  );

  const refreshListAfterPayment = useCallback(
    (conditions, page, rows) => getList(conditions, page, rows),
    [getList]
  );

  const [paymentManagementDataId, setPaymentManagementDataId] =
    useState(null);

  const [currentStatementNumber, setCurrentStatementNumber] = useState(null);
  const [currentDataId, setCurrentDataId] = useState(null);
  const [originStatementNumber, setOriginStatementNumber] = useState(null);
  const [codes, setCodes] = useState({});
  const [exporting, setExporting] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [menuItem, setMenuItem] = useState(null);
  /** PrimeReact dataKey 기준 펼침 행 (lazy 페이징과 분리) */
  const [expandedRows, setExpandedRows] = useState({});
  const [expandedGroupRows, setExpandedGroupRows] = useState({});
  const [loadingGroupMembers, setLoadingGroupMembers] = useState(() => new Set());

  const collapseAllGroupRows = useCallback(() => {
    setExpandedRows({});
  }, []);

  const toggleRowExpand = useCallback(
    async (rowData, event) => {
      event?.stopPropagation?.();
      const rowKey = String(rowData.dataId);
      const statementNumber = rowData.statementNumber;

      if (expandedRows[rowKey]) {
        const next = { ...expandedRows };
        delete next[rowKey];
        setExpandedRows(next);
        return;
      }

      if (!expandedGroupRows[statementNumber]) {
        setLoadingGroupMembers((prev) => new Set(prev).add(statementNumber));
        try {
          const params = formatPerformanceCheckListQueryParams(searchConditions);
          const {
            data: { list },
          } = await performanceCheckService.listGroupMembers(statementNumber, {
            ...params,
            excludeDataId: rowData.dataId,
          });
          setExpandedGroupRows((prev) => ({
            ...prev,
            [statementNumber]: list || [],
          }));
        } catch (error) {
          window.cerp.dialog.error(
            '이력 조회 실패',
            `[${error?.code}] ${error?.message}`
          );
          return;
        } finally {
          setLoadingGroupMembers((prev) => {
            const next = new Set(prev);
            next.delete(statementNumber);
            return next;
          });
        }
      }

      setExpandedRows((prev) => ({ ...prev, [rowKey]: true }));
    },
    [expandedRows, expandedGroupRows, searchConditions]
  );

  async function exportToExcel() {
    setExporting(true);

    const params = {
      ...searchConditions,
      page: lazyParams.page + 1,
      size: lazyParams.rows,
    };
    params.startDate = dayjs(searchConditions.startDate).format('YYYYMMDD');
    params.endDate = dayjs(searchConditions.endDate).format('YYYYMMDD');

    await performanceCheckService.exportExcel(params);

    setExporting(false);
  }

  function checkAuth(userInfo, type) {
    const { roleCode } = userInfo;

    const ACCESS_PERMISSION = {
      regist: _.filter(UserRoleType, function (r) {
        return (
          _.startsWith(r.value, 'P') ||
          _.startsWith(r.value, 'S') ||
          r.value === 'A_MST' ||
          r.value === 'CERP_ADM' ||
          r.value === 'E_OFF'
        );
      }),
      update: _.filter(UserRoleType, function (r) {
        return (
          _.startsWith(r.value, 'C_') ||
          r.value === 'A_OFF' ||
          r.value === 'E_ADM'
        );
      }),
      getDeleteList: _.filter(UserRoleType, function (r) {
        return (
          r.value === 'CERP_ADM' || r.value === 'A_MST' || r.value === 'A_ADM'
        );
      }),
      getMonthlyList: _.filter(UserRoleType, function (r) {
        return r.value === 'CERP_ADM' || _.startsWith(r.value, 'A_');
      }),
    };

    return _.findIndex(ACCESS_PERMISSION[type], { value: roleCode }) >= 0;
  }

  const handleSelectAll = (
    selected,
    field,
    updateCodes,
    updateSearchConditions
  ) => {
    const update = [];

    if (selected && selected.includes('')) {
      update.push(
        ..._.get(codes, `${field}`).map((option) => {
          if (option.value !== '') {
            option.disabled = true;
          }
          return option;
        })
      );
      setCodes((ps) => ({ ...ps, [field]: update, ...updateCodes }));
      setSearchConditions((ps) => ({
        ...ps,
        [field]: [_.get(codes[field], '0.value')],
        ...updateSearchConditions,
      }));
      return;
    }

    update.push(
      ..._.get(codes, `${field}`).map((option) => {
        if (option.disabled) delete option.disabled;
        return option;
      })
    );
    setCodes((ps) => ({ ...ps, [field]: update, ...updateCodes }));

    if (selected === null) {
      setSearchConditions((ps) => ({ ...ps, [field]: '' }));
      return;
    }

    setSearchConditions((ps) => ({
      ...ps,
      [field]: selected,
      ...updateSearchConditions,
    }));
  };

  useEffect(() => {
    (async () => {
      if (data.length) {
        const [insurances] = await CodeUtil.getInsuranceCodes({});
        setCodes((ps) => ({ ...ps, insurances }));
      }
    })();

    return () => {
      setCurrentDataId(null);
      setCurrentStatementNumber(null);
    };
  }, [data]);

  useEffect(() => {
    setSearchConditions((ps) => ({ ...ps, ...defaultSearchConditions }));

    window.getListByChildren = async function () {
      setLazyParams({ ...DEFAULT_LAZY_PARAMS });
      await getList(
        defaultSearchConditions,
        _.get(DEFAULT_LAZY_PARAMS, 'page'),
        _.get(DEFAULT_LAZY_PARAMS, 'rows')
      );
    };

    return () => {
      delete window.getListByChildren;
    };
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultSearchConditions]);

  useEffect(() => {
    if (dateRangeResetKey === 0) return;
    const cond = defaultSearchConditionsRef.current;
    getList(cond, DEFAULT_LAZY_PARAMS.page, DEFAULT_LAZY_PARAMS.rows);
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRangeResetKey]);

  useEffect(() => {
    setCodes((ps) => ({
      ...ps,
      associations,
      enterprises,
      shops: filterShopsForPerformanceCheckList(shops),
    }));
  }, [shops, enterprises, associations]);

  const [isViewRock, setIsViewRock] = useState(true);
  useEffect(() => {
    if (myShopName) {
      if (myShopName?.includes('송이')) {
        setIsViewRock(false);
      } else {
        setIsViewRock(true);
      }
    }
  }, [myShopName]);

  useEffect(() => {
    if (myRoleCode) {
      if (myRoleCode === '6_ADM') {
        history.push('/sales/settlement/safe6');
      }
    }
  }, [myRoleCode, history]);

  const [paymentSummary, setPaymentSummary] = useState(null);
  useEffect(() => {
    if (myAssociationId) {
      const getPaymentSummary = async () => {
        const today = dayjs();
        const data = await performanceCheckPaymentService.summary({
          startDate: today.format('YYYYMMDD'),
          endDate: today.format('YYYYMMDD'),
          associations: [myAssociationId],
          enterprises: [myEnterpriseId],
          shops: [myShopId],
          page: 1,
          size: 999,
        });
        if (data) {
          setPaymentSummary(data.data);
        }
      };
      getPaymentSummary();
    }
  }, [myAssociationId, myEnterpriseId, myShopId]);

  const renderPerformanceCheckListColumns = (variant = 'main') => {
    const isNested = variant === 'nested';

    // DataTable은 Column이 직접 children이어야 함 (Fragment 사용 시 빈 행만 렌더됨)
    return [
      !isNested ? (
        <Column
          key="group-toggle"
          header=""
          align="center"
          style={{ width: '3rem' }}
          body={(rowData) => {
            const groupCount = Number(rowData.groupCount) || 1;
            if (groupCount <= 1) {
              return null;
            }
            const rowKey = String(rowData.dataId);
            const expanded = !!expandedRows[rowKey];
            const isLoading = loadingGroupMembers.has(rowData.statementNumber);
            return (
              <Button
                type="button"
                icon={
                  isLoading
                    ? 'pi pi-spin pi-spinner'
                    : expanded
                      ? 'pi pi-minus'
                      : 'pi pi-plus'
                }
                className="p-button-text p-button-sm pcr-group-toggle"
                disabled={isLoading}
                title={
                  expanded
                    ? `동일 성능번호 ${groupCount}건 접기`
                    : `동일 성능번호 ${groupCount - 1}건 더 보기`
                }
                aria-label={expanded ? '이전 이력 접기' : '이전 이력 펼치기'}
                onClick={(e) => toggleRowExpand(rowData, e)}
              />
            );
          }}
        />
      ) : null,
      <Column
        key="row-index"
        field="dtn"
        header="순번"
        align="center"
        className="text-lg"
        body={(rowData, { rowIndex }) =>
          isNested ? (
            <span className="text-color-secondary text-sm">└</span>
          ) : (
            <span>{lazyParams.first + rowIndex + 1}</span>
          )
        }
      />,
      <Column
        key="license-plate"
        field="licensePlateNumber"
          header={
            <div className="text-lg text-center">
              차량번호
              <br />
              차량모델
            </div>
          }
          body={({ licensePlateNumber, carName }) => (
            <div className="w-10rem text-lg">
              {licensePlateNumber}
              <br />
              <p
                className="w-10rem text-md text-left"
                style={{
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  wordBreak: 'break-all',
                  whiteSpace: 'normal',
                }}
              >
                {carName}
              </p>
            </div>
          )}
      />,
      <Column
        key="customer"
        field="customerCompanyName"
          header={
            <div className="text-lg text-left">
              소속상사
              <br />
              고객명
            </div>
          }
          body={({ customerCompanyName, customerName }) => (
            <div className="text-lg">
              {customerCompanyName}
              <br />
              {customerName}
            </div>
          )}
      />,
      <Column
        key="statement-number"
        field="statementNumber"
        align="center"
        header="성능지 번호"
          className="text-lg"
          body={(rowData) => {
            const { dataId, statementNumber } = rowData;
            const groupCount = Number(rowData.groupCount) || 1;
            const groupBadge =
              !isNested && groupCount > 1 && !expandedRows[String(dataId)] ? (
                <span className="pcr-group-count-badge">+{groupCount - 1}</span>
              ) : null;
            return (
              <div className="flex justify-content-center align-items-center gap-1">
                {checkAuth(myInfoLoadable.contents, 'update') ? (
                  <div className="flex align-items-center text-lg">
                    <span>{statementNumber}</span>
                    {groupBadge}
                    <CopyButton text={statementNumber} />
                  </div>
                ) : (
                  <div className="flex align-items-center text-lg">
                    <span
                      className="cursor-pointer text-primary"
                      onClick={() => {
                        setCurrentStatementNumber(statementNumber);
                        setCurrentDataId(dataId);
                      }}
                    >
                      {statementNumber}
                    </span>
                    {groupBadge}
                    <CopyButton text={statementNumber} />
                  </div>
                )}
              </div>
            );
          }}
      />,
      <Column
        key="print-menu"
        field="statementNumber"
        className="text-lg text-center"
          header={
            <div className="text-lg">
              출력
              <br />
              재성능
            </div>
          }
          body={(rowData) => (
            <Button
              icon="pi pi-bars"
              className="p-button-sm p-button-outlined"
              onClick={(event) => {
                setMenuItem(rowData);
                menu.current.toggle(event);
              }}
              aria-haspopup
              aria-controls="overlay_tmenu"
            />
          )}
      />,
      <Column
        key="payment"
        header="수납관리"
        className="text-center text-lg"
          body={({ paymentDataId, insurancePriceRefundedAt }) => {
            const refundDone =
              insurancePriceRefundedAt != null &&
              String(insurancePriceRefundedAt).trim() !== '';
            if (refundDone) {
              return (
                <span className="pcr-payment-return-done">반환완료</span>
              );
            }
            return (
              <Button
                type="button"
                label="반환"
                icon="pi pi-wallet"
                className="p-button-sm p-button-outlined pcr-payment-return-btn"
                disabled={paymentDataId == null}
                title={
                  paymentDataId == null
                    ? '연결된 결제 데이터가 없습니다'
                    : undefined
                }
                onClick={() => setPaymentManagementDataId(paymentDataId)}
              />
            );
          }}
      />,
      <Column
        key="is-recheck"
        field="isRecheck"
        header="재성능여부"
          className="text-center text-lg"
          body={({ isRecheck }) =>
            isRecheck === YN.YES && <Tag className="text-sm" value="재성능" />
          }
      />,
      <Column
        key="check-date"
        field="checkDate"
        header="점검일자"
          align="center"
          className="text-lg"
          body={({ checkDate }) => (
            <p className="text-lg">{dayjs(checkDate).format('YYYY-MM-DD')}</p>
          )}
      />,
      <Column
        key="car-year"
        field="carYear"
        className="text-lg"
          header={
            <div className="text-lg text-center">
              차량연식
              <br />
              주행거리
            </div>
          }
          body={({ carYear, carDistance }) => (
            <div className="text-lg">
              {carYear} 년식
              <br />
              {columnNumberWithCommas(carDistance)} Km
            </div>
          )}
      />,
      <Column
        key="check-date-diff"
        field="checkDateDiff"
        header="경과일수"
          align="center"
          className="text-lg"
          body={({ checkDateDiff }) => (
            <p className="text-lg">{`${checkDateDiff}일`}</p>
          )}
      />,
      <Column
        key="check-state"
        field="checkState"
        align="center"
          className="text-lg"
          header="점검상태"
          body={({ checkState }) => (
            <p className="text-lg">{columnCheckState(checkState)}</p>
          )}
      />,
      <Column
        key="transfer-state"
        field="transferState"
        header="전송상태"
          align="center"
          className="text-lg"
          body={({ transferState }) => (
            <p className="text-lg">{columnTransferState(transferState)}</p>
          )}
      />,
      <Column
        key="insurance-price"
        field="insurancePrice"
        header={
            <div className="text-lg text-center">
              보험료
              <br />
              (부가세포함)
            </div>
          }
          align="center"
          className="text-lg"
          body={({ assuranceType, insurancePrice }) => (
            <p className="text-lg">
              {assuranceType === '1'
                ? '자가보증'
                : insurancePrice
                  ? priceFormatter.format(Math.round(insurancePrice * 1.1))
                  : '-'}
            </p>
          )}
      />,
    ].filter(Boolean);
  };

  return (
    <div className="grid pcr">
      <div className="col-12">
        {(() => {
          switch (myInfoLoadable.state) {
            case 'hasValue':
              const myInfo = MyInfoUtil.unpack(myInfoLoadable.contents);
              return (
                <>
                  <SearchMenu
                    onInit={() => {
                      clearPersistedPerformanceCheckListDates();
                      setDateRangeResetKey((k) => k + 1);
                      setLazyParams({ ...DEFAULT_LAZY_PARAMS });
                      setSelectedRows([]);
                    }}
                    onSearch={() => {
                      runSearchWithPersistedDates(searchConditions);
                      setSelectedRows([]);
                      setLazyParams({ ...DEFAULT_LAZY_PARAMS });
                    }}
                  >
                    <div
                      className={classNames('field lg:col-4 md:col-12', {
                        'sm:col-6 md:col-12 lg:col-4': checkAuth(
                          myInfoLoadable.contents,
                          'getDeleteList'
                        ),
                        'sm:col-6 md:col-12 lg:col-8': !checkAuth(
                          myInfoLoadable.contents,
                          'getDeleteList'
                        ),
                      })}
                    >
                      <label
                        htmlFor="range"
                        className="font-bold text-lg md:text-base"
                      >
                        성능점검일자
                      </label>
                      <RangeCalendar
                        startDate={searchConditions.startDate}
                        endDate={searchConditions.endDate}
                        onStartDateChanged={(date) => {
                          setSearchConditions((ps) => ({
                            ...ps,
                            startDate: date,
                          }));
                        }}
                        onEndDateChanged={(date) => {
                          setSearchConditions((ps) => ({
                            ...ps,
                            endDate: date,
                          }));
                        }}
                        touchUI={touchUI}
                        showNavigators={true}
                      />
                    </div>
                    <div className="field col-12 sm:col-6 md:col-4 lg:col-4">
                      <label
                        htmlFor="checkType"
                        className="font-bold text-lg md:text-base"
                      >
                        점검종류
                      </label>
                      <div className="flex flex-auto flex-wrap align-items-center justify-content-start mt-2 gap-3">
                        <div className="flex flex-auto flex-grow-0 flex-shrink-0 align-items-center justify-content-start gap-1">
                          <RadioButton
                            inputId="radio_checkType_all"
                            name="checkType"
                            value=""
                            onChange={(e) => {
                              setSearchConditions((ps) => ({
                                ...ps,
                                checkType: e.value,
                              }));
                            }}
                            checked={searchConditions.checkType === ''}
                          />
                          <label htmlFor="radio_checkType_all">전체</label>
                        </div>
                        <div className="flex flex-auto flex-grow-0 flex-shrink-0 align-items-center justify-content-start gap-1">
                          <RadioButton
                            inputId="radio_checkType_new"
                            name="checkType"
                            value="N"
                            onChange={(e) => {
                              setSearchConditions((ps) => ({
                                ...ps,
                                checkType: e.value,
                              }));
                            }}
                            checked={searchConditions.checkType === 'N'}
                          />
                          <label htmlFor="radio_checkType_new">신규성능</label>
                        </div>
                        <div className="flex flex-auto flex-grow-0 flex-shrink-0 align-items-center justify-content-start gap-1">
                          <RadioButton
                            inputId="radio_checkType_re"
                            name="checkType"
                            value="Y"
                            onChange={(e) => {
                              setSearchConditions((ps) => ({
                                ...ps,
                                checkType: e.value,
                              }));
                            }}
                            checked={searchConditions.checkType === 'Y'}
                          />
                          <label htmlFor="radio_checkType_re">재성능</label>
                        </div>
                      </div>
                    </div>
                    <div
                      className={classNames('field col-12', {
                        'sm:col-6 md:col-4 lg:col-4': checkAuth(
                          myInfoLoadable.contents,
                          'getDeleteList'
                        ),
                        hidden: !checkAuth(
                          myInfoLoadable.contents,
                          'getDeleteList'
                        ),
                      })}
                    >
                      <>
                        {/* <label htmlFor="delYN">삭제여부</label>
                        <div className="flex flex-auto flex-wrap align-items-center justify-content-start mt-2 gap-3">
                          <div className="flex flex-auto flex-grow-0 flex-shrink-0 align-items-center justify-content-start gap-1">
                            <RadioButton
                              inputId="radio_delYn_a"
                              name="delYN"
                              value=""
                              onChange={(e) => {
                                setSearchConditions((ps) => ({
                                  ...ps,
                                  delYN: e.value,
                                }));
                                // setLazyParams({ ...DEFAULT_LAZY_PARAMS });
                              }}
                              checked={searchConditions.delYN === ''}
                            />
                            <label htmlFor="radio_delYn_a">전체</label>
                          </div>
                          <div className="flex flex-auto flex-grow-0 flex-shrink-0 align-items-center justify-content-start gap-1">
                            <RadioButton
                              inputId="radio_delYn_y"
                              name="delYN"
                              value="Y"
                              onChange={(e) => {
                                setSearchConditions((ps) => ({
                                  ...ps,
                                  delYN: e.value,
                                }));
                                // setLazyParams({ ...DEFAULT_LAZY_PARAMS });
                              }}
                              checked={searchConditions.delYN === 'Y'}
                            />
                            <label htmlFor="radio_delYn_y">삭제</label>
                          </div>
                          <div className="flex flex-auto flex-grow-0 flex-shrink-0 align-items-center justify-content-start gap-1">
                            <RadioButton
                              inputId="radio_delYn_n"
                              name="delYN"
                              value="N"
                              onChange={(e) => {
                                setSearchConditions((ps) => ({
                                  ...ps,
                                  delYN: e.value,
                                }));
                                // setLazyParams({ ...DEFAULT_LAZY_PARAMS });
                              }}
                              checked={searchConditions.delYN === 'N'}
                            />
                            <label htmlFor="radio_delYn_n">미삭제</label>
                          </div>
                        </div> */}
                      </>
                    </div>
                    {/* <div className="field col-12 sm:col-6 lg:col-4">
                      <label
                        htmlFor="associations"
                        className="font-bold text-lg md:text-base"
                      >
                        소속협회
                      </label>
                      <MultiSelect
                        className="w-full"
                        options={codes.associations}
                        display="chip"
                        placeholder="전체 협회"
                        value={searchConditions.associations}
                        filter
                        showSelectAll={false}
                        onChange={async ({ value }) => {
                          const [enterprises, enterpriseId, enterprise] =
                            await CodeUtil.getEnterpriseCodes(myInfo, {
                              isParentEmpty: _.isEmpty(
                                _.get(codes, 'associations')
                              ),
                              associationId: value,
                            });
                          const [shops, shopId, shop] =
                            await CodeUtil.getShopCodes(myInfo, {
                              isParentEmpty: _.isEmpty(enterprises),
                            });

                          handleSelectAll(
                            value,
                            'associations',
                            {
                              enterprises,
                              shops,
                            },
                            {
                              enterprises: [],
                              shops: [],
                            }
                          );
                        }}
                      />
                    </div>
                    <div className="field col-12 sm:col-6 lg:col-4">
                      <label
                        htmlFor="enterprises"
                        className="font-bold text-lg md:text-base"
                      >
                        소속점검법인
                      </label>
                      <MultiSelect
                        className="w-full"
                        options={codes.enterprises}
                        display="chip"
                        placeholder="전체 업체"
                        value={searchConditions.enterprises}
                        filter
                        showSelectAll={false}
                        onChange={async ({ value }) => {
                          const [shops, shopId, shop] =
                            await CodeUtil.getShopCodes(myInfo, {
                              isParentEmpty: _.isEmpty(
                                _.get(codes, 'enterprises')
                              ),
                              enterpriseId: value,
                            });

                          handleSelectAll(
                            value,
                            'enterprises',
                            {
                              shops,
                            },
                            {
                              shops: [],
                            }
                          );
                        }}
                      />
                    </div>
                    <div className="field col-12 sm:col-6 lg:col-4">
                      <label
                        htmlFor="shops"
                        className="font-bold text-lg md:text-base"
                      >
                        소속점검장
                      </label>
                      <MultiSelect
                        className="w-full"
                        options={codes.shops}
                        display="chip"
                        placeholder="전체 점검장"
                        value={searchConditions.shops}
                        filter
                        showSelectAll={false}
                        onChange={({ value }) => {
                          handleSelectAll(value, 'shops');
                        }}
                      />
                    </div> */}
                    <div className="field col-12 sm:col-6 lg:col-4">
                      <label
                        htmlFor="customerSearchKeyword"
                        className="font-bold text-lg md:text-base"
                      >
                        고객명 검색
                      </label>
                      <InputText
                        className="w-full text-lg md:text-base"
                        value={searchConditions.customerSearchKeyword}
                        placeholder={
                          searchConditions.customerSearchBy === '1'
                            ? '고객 이름으로 검색'
                            : '매매상사 이름, 대표전화, 사업자등록번호로 검색'
                        }
                        onChange={(e) => {
                          setSearchConditions((ps) => ({
                            ...ps,
                            customerSearchKeyword: e.target.value,
                          }));
                        }}
                        onKeyDown={(e) =>
                          e.key === 'Enter'
                            ? runSearchWithPersistedDates(searchConditions)
                            : null
                        }
                      />
                      {/* <div className="flex flex-auto flex-wrap align-items-center justify-content-start mt-2 gap-3">
                          <div className="flex flex-auto flex-grow-0 flex-shrink-0 align-items-center justify-content-start gap-1">
                            <RadioButton
                              inputId="radio_dealerName"
                              name="customerSearchBy"
                              value="1"
                              onChange={(e) => {
                                setSearchConditions((ps) => ({
                                  ...ps,
                                  customerSearchBy: e.value,
                                }));
                                // setLazyParams({ ...DEFAULT_LAZY_PARAMS });
                              }}
                              checked={searchConditions.customerSearchBy === '1'}
                            />
                            <label htmlFor="radio_dealerName">고객</label>
                          </div>
                          <div className="flex flex-auto flex-grow-0 flex-shrink-0 align-items-center justify-content-start gap-1">
                            <RadioButton
                              inputId="radio_companyName"
                              name="customerSearchBy"
                              value="2"
                              onChange={(e) => {
                                setSearchConditions((ps) => ({
                                  ...ps,
                                  customerSearchBy: e.value,
                                }));
                                // setLazyParams({ ...DEFAULT_LAZY_PARAMS });
                              }}
                              checked={searchConditions.customerSearchBy === '2'}
                            />
                            <label htmlFor="radio_companyName">매매상사</label>
                          </div>
                      </div> */}
                    </div>
                    <div className="field col-12 sm:col-6 lg:col-4">
                      <label
                        htmlFor="range"
                        className="font-bold text-lg md:text-base"
                      >
                        차량
                      </label>
                      <InputText
                        className="w-full text-lg md:text-base"
                        value={searchConditions.carSearchKeyword}
                        placeholder="차량번호, 차대번호로 검색"
                        onChange={(e) => {
                          setSearchConditions((ps) => ({
                            ...ps,
                            carSearchKeyword: e.target.value,
                          }));
                          // setLazyParams({ ...DEFAULT_LAZY_PARAMS });
                        }}
                        onKeyDown={(e) =>
                          e.key === 'Enter'
                            ? runSearchWithPersistedDates(searchConditions)
                            : null
                        }
                      />
                    </div>
                    <div className="field col-12 sm:col-6 lg:col-4">
                      <label
                        htmlFor="statementNumber"
                        className="font-bold text-lg md:text-base"
                      >
                        성능지번호
                      </label>
                      <InputText
                        id="statementNumber"
                        className="w-full text-lg md:text-base"
                        value={searchConditions.statementNumber}
                        onChange={(e) => {
                          setSearchConditions((ps) => ({
                            ...ps,
                            statementNumber: e.target.value,
                          }));
                          // setLazyParams({ ...DEFAULT_LAZY_PARAMS });
                        }}
                        placeholder="성능지번호로 검색"
                        onKeyDown={(e) =>
                          e.key === 'Enter'
                            ? runSearchWithPersistedDates(searchConditions)
                            : null
                        }
                      />
                    </div>
                  </SearchMenu>

                  <div className="card flex flex-auto flex-wrap align-items-center justify-content-between gap-1">
                    <div className="flex  gap-1">
                      {paymentSummary && (
                        <div className="flex flex-wrap align-items-center text-lg font-bold gap-2">
                          <i className="pi pi-tags" />
                          당일 총 합계{' '}
                          <div>
                            <span className="text-primary">
                              {priceFormatter.format(
                                paymentSummary.sumTotalPrice
                              ) ?? 0}
                            </span>{' '}
                            원
                          </div>
                          <div>/</div>
                          <div>
                            총 점검료{' '}
                            <span className="text-primary">
                              {priceFormatter.format(
                                paymentSummary.sumTotalCheckPrice
                              ) ?? 0}
                            </span>{' '}
                            원
                          </div>
                          <div>/</div>
                          <div>
                            총 보험료{' '}
                            <span className="text-primary">
                              {priceFormatter.format(
                                paymentSummary.sumTotalInsurancePrice
                              ) ?? 0}
                            </span>{' '}
                            원
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="flex gap-1">
                      {checkAuth(myInfoLoadable.contents, 'regist') && (
                        <>
                          <Button
                            label="성능점검 신규 등록"
                            icon="pi pi-check-square"
                            style={{ marginLeft: 'auto' }}
                            onClick={() => {
                              CommonUtil.Window.popup(
                                '/performance-check/form'
                              );
                            }}
                            className="text-lg md:text-base"
                          />
                        </>
                      )}

                      <Button
                        label="엑셀 다운로드"
                        className="p-button-outlined text-lg md:text-base"
                        icon="pi pi-download"
                        disabled={exporting || !data.length}
                        loading={exporting}
                        onClick={async () => await exportToExcel()}
                      />
                    </div>
                  </div>

                  <Panel className="shadow-1 mb-3">
                    <TieredMenu
                      ref={menu}
                      id="overlay_tmenu"
                      popup
                      model={
                        checkAuth(myInfoLoadable.contents, 'update')
                          ? [
                              {
                                label: '성능지출력',
                                icon: 'pi pi-fw pi-print',
                                disabled: ![
                                  TransferState.Transfered,
                                  TransferState.Updated,
                                  TransferState.TransferReturn,
                                ].includes(_.get(menuItem, 'transferState')),
                                command: (event) => {
                                  const { statementNumber, dataId } = menuItem;
                                  CommonUtil.Window.popup(
                                    `/performance-check/${statementNumber}-${dataId}/viewer?docType=performance`,
                                    `pcv-print#${statementNumber}`,
                                    window.innerWidth * 0.8,
                                    window.innerHeight * 0.8
                                  );

                                  menu.current.hide(event);
                                },
                              },
                              {
                                label: '보험증명서 출력',
                                icon: 'pi pi-fw pi-print',
                                disabled: ![
                                  TransferState.Transfered,
                                  TransferState.Updated,
                                  TransferState.TransferReturn,
                                ].includes(_.get(menuItem, 'transferState')),
                                command: (event) => {
                                  const { statementNumber, dataId } = menuItem;
                                  CommonUtil.Window.popup(
                                    `/performance-check/${statementNumber}-${dataId}/viewer?docType=insurance`,
                                    `pcv-insurance#${statementNumber}`,
                                    window.innerWidth * 0.8,
                                    window.innerHeight * 0.8
                                  );

                                  menu.current.hide(event);
                                },
                              },
                              {
                                label: '재성능 등록',
                                icon: 'pi pi-fw pi-clone',
                                disabled: !(
                                  _.get(menuItem, 'transferState') ===
                                    TransferState.Transfered ||
                                  _.get(menuItem, 'transferState') ===
                                    TransferState.Updated ||
                                  _.get(menuItem, 'transferState') ===
                                    TransferState.TransferReturn
                                ),
                                command: (event) => {
                                  const { statementNumber, dataId } = menuItem;
                                  setCurrentDataId(dataId);
                                  setOriginStatementNumber(statementNumber);
                                  menu.current.hide(event);
                                },
                              },
                            ]
                          : [
                              {
                                label: '성능지출력',
                                icon: 'pi pi-fw pi-print',
                                disabled: isViewRock
                                  ? ![
                                      TransferState.Transfered,
                                      TransferState.Updated,
                                      TransferState.TransferReturn,
                                    ].includes(_.get(menuItem, 'transferState'))
                                  : false,
                                command: (event) => {
                                  const { statementNumber, dataId } = menuItem;
                                  CommonUtil.Window.popup(
                                    `/performance-check/${statementNumber}-${dataId}/viewer?docType=performance`,
                                    `pcv-print#${statementNumber}`,
                                    window.innerWidth * 0.8,
                                    window.innerHeight * 0.8
                                  );

                                  menu.current.hide(event);
                                },
                              },
                              {
                                label: '보험증명서 출력',
                                icon: 'pi pi-fw pi-print',
                                disabled: isViewRock
                                  ? ![
                                      TransferState.Transfered,
                                      TransferState.Updated,
                                      TransferState.TransferReturn,
                                    ].includes(_.get(menuItem, 'transferState'))
                                  : false,
                                command: (event) => {
                                  const { statementNumber, dataId } = menuItem;
                                  CommonUtil.Window.popup(
                                    `/performance-check/${statementNumber}-${dataId}/viewer?docType=insurance`,
                                    `pcv-insurance#${statementNumber}`,
                                    window.innerWidth * 0.8,
                                    window.innerHeight * 0.8
                                  );

                                  menu.current.hide(event);
                                },
                              },
                              {
                                label: '재성능 등록',
                                icon: 'pi pi-fw pi-clone',
                                disabled: !(
                                  // 전송 완료 상태에서만 재성능 등록 가능
                                  (
                                    _.get(menuItem, 'transferState') ===
                                      TransferState.Transfered ||
                                    _.get(menuItem, 'transferState') ===
                                      TransferState.Updated ||
                                    _.get(menuItem, 'transferState') ===
                                      TransferState.TransferReturn
                                  )
                                ),
                                command: (event) => {
                                  const { statementNumber, dataId } = menuItem;
                                  setCurrentDataId(dataId);
                                  setOriginStatementNumber(statementNumber);
                                  menu.current.hide(event);
                                },
                              },
                            ]
                      }
                      onHide={() => setMenuItem(null)}
                    />
                    <DataTable
                      loading={loading}
                      value={data}
                      dataKey="dataId"
                      lazy
                      rows={lazyParams.rows}
                      first={lazyParams.first}
                      totalRecords={totalRecords}
                      paginator
                      responsiveLayout="scroll"
                      paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                      currentPageReportTemplate="전체 {totalRecords}건 중 {first} ~ {last}"
                      rowsPerPageOptions={COUNT_PER_PAGE}
                      resizableColumns
                      showGridlines
                      expandedRows={expandedRows}
                      onRowToggle={(e) => setExpandedRows(e.data)}
                      rowExpansionTemplate={(rowData) => {
                        const statementNumber = rowData.statementNumber;
                        const members = expandedGroupRows[statementNumber];
                        const isLoading = loadingGroupMembers.has(statementNumber);

                        if (isLoading) {
                          return (
                            <div className="pcr-group-expand-panel">
                              <ProgressBar mode="indeterminate" style={{ height: '4px' }} />
                            </div>
                          );
                        }

                        if (!members?.length) {
                          return (
                            <div className="pcr-group-expand-panel p-3 text-center text-color-secondary">
                              표시할 이전 이력이 없습니다.
                            </div>
                          );
                        }

                        return (
                          <div className="pcr-group-expand-panel">
                            <DataTable
                              value={members}
                              dataKey="dataId"
                              showGridlines
                              responsiveLayout="scroll"
                              className="pcr-group-members-datatable pcr-group-members-datatable--no-header"
                            >
                              {renderPerformanceCheckListColumns('nested')}
                            </DataTable>
                          </div>
                        );
                      }}
                      onPage={async (event) => {
                        collapseAllGroupRows();
                        setLazyParams(event);
                        await getList(searchConditions, event.page, event.rows);
                      }}
                      selection={selectedRows}
                      selectionMode="checkbox"
                      onSelectionChange={({ value }) => {
                        setSelectedRows(value);
                      }}
                      emptyMessage="데이터가 없습니다."
                    >
                      {renderPerformanceCheckListColumns('main')}
                    </DataTable>
                  </Panel>

                  {paymentManagementDataId != null && (
                    <PaymentDialog
                      visible
                      onHide={() => setPaymentManagementDataId(null)}
                      dataId={paymentManagementDataId}
                      getData={refreshListAfterPayment}
                      lazyParams={lazyParams}
                      searchConditions={searchConditions}
                      isLoading={loading}
                    />
                  )}

                  {currentStatementNumber !== null && (
                    <CheckBaseDataDialog
                      visible
                      userInfo={myInfoLoadable.contents}
                      dataId={currentDataId}
                      statementNumber={currentStatementNumber}
                      onHide={async () => {
                        setCurrentStatementNumber(null);
                        setCurrentDataId(null);
                        await getList(
                          searchConditions,
                          lazyParams.page,
                          lazyParams.rows
                        );
                        setSelectedRows([]);
                      }}
                    />
                  )}

                  {originStatementNumber !== null && (
                    <RecheckBaseDataDialog
                      visible
                      userInfo={myInfoLoadable.contents}
                      dataId={currentDataId}
                      originStatementNumber={originStatementNumber}
                      onHide={async () => {
                        setOriginStatementNumber(null);
                        setCurrentDataId(null);
                        await getList(
                          searchConditions,
                          lazyParams.page,
                          lazyParams.rows
                        );
                        setSelectedRows([]);
                      }}
                    />
                  )}
                </>
              );
            case 'loading':
              return <ProgressBar mode="indeterminate" />;

            case 'hasError':
              return null;

            default:
          }
        })()}
      </div>
    </div>
  );
};

const comparisonFn = function (prevProps, nextProps) {
  return prevProps.location.pathname === nextProps.location.pathname;
};

export default React.memo(PerformanceCheckList, comparisonFn);
